import React, {useEffect, useState, useRef} from 'react';
import Table from 'components/table/Table';
import TableHeader from 'components/table/TableHeader';
import TableRow from 'components/table/TableRow';
import TableCell from 'components/table/TableCell';
import WhiteButton from 'components/WhiteButton';
import BlueButton from 'components/BlueButton';
import DownloadIcon from 'assets/images/icons/DownloadIcon';
import SearchIcon from 'assets/images/icons/SearchIcon';
import DotsMenu from 'components/DotsMenu';
import CustomSelect from 'components/inputs/CustomSelect';
import {SearchType, SearchField} from 'types/search';
import {UserHrDocument} from 'types/userHrDocument';
import {UserTraining} from 'types/userTraining';
import {UserApplication} from '../../types/userApplication';
import {UserEvaluation} from 'types/userEvaluation';
import {UserJobRole} from 'types/userJobRole';
import {DocumentReportService} from '../../services/documentReportService';
import {UserHrDocumentService} from '../../services/userHrDocumentService';
import {UserTrainingService} from '../../services/userTrainingService';
import {UserApplicationService} from '../../services/userApplicationService';
import {UserEvaluationService} from '../../services/userEvaluationServices';
import {UserJobRoleService} from '../../services/userJobRoleService';
import {DocumentTemplate} from 'types/documentTemplate';
import DocumentTemplateRenderer from 'components/documentTemplate/DocumentTemplateRenderer';
import {useSelector, useDispatch} from 'react-redux';
import {RootState, AppDispatch} from '../../store/store';
import {Provider} from 'react-redux';
import {store} from '../../store/store';
import {createRoot} from 'react-dom/client';
import DownloadModal from '../../components/DownloadModal';
import ViewIcon from '../../assets/images/icons/ViewIcon';
import DateRangeInput from '../../components/inputs/DateRangeInput';
import {parse, format} from 'date-fns';
import StatusBadge from '../../components/StatusBadge';
import {JobRoleThunks} from '../jobRole/jobRoleThunks';
import {JobRole} from 'types/jobRole';

interface ReportFinderTableProps {
  openViewerModal: (record: any) => void;
  openDrawer: () => void;
}

type RecordWithType<T> = T & {documentType: string};

type DownloadStatus = 'preparing' | 'processing' | 'downloading' | 'complete';

const ReportFinderTable: React.FC<ReportFinderTableProps> = ({
  openViewerModal,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [selectedRecords, setSelectedRecords] = useState<Array<any>>([]);
  const [isAllSelected, setIsAllSelected] = useState<boolean>(false);
  const [showTable, setShowTable] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [dateRangeValue, setDateRangeValue] = useState<string>('');
  const [allRecords, setAllRecords] = useState<
    Array<
      RecordWithType<
        | UserHrDocument
        | UserTraining
        | UserApplication
        | UserEvaluation
        | UserJobRole
      >
    >
  >([]);
  const [totalCount, setTotalCount] = useState<number>(0);

  const [selectedStatus, setSelectedStatus] = useState('all');

  const statusOptions = [
    {label: 'All Status', value: 'all'},
    {label: 'Complete', value: 'complete'},
    {label: 'Missing', value: 'missing'},
    {label: 'Pending', value: 'pending_review'},
    {label: 'Expired', value: 'expired'},
    {label: 'Expiring Soon', value: 'expiring_soon'},
  ];

  const [selectedDocumentType, setSelectedDocumentType] = useState('all');

  const documentTypeOptions = [
    {label: 'All Types', value: 'all'},
    {label: 'Role', value: 'Role'},
    {label: 'Application', value: 'Application'},
    {label: 'Document', value: 'Document'},
    {label: 'Training', value: 'Training'},
    {label: 'Credential', value: 'Credential'},
  ];

  const [selectedJobRole, setSelectedJobRole] = useState('all');

  const [searchTrigger, setSearchTrigger] = useState(0);

  const [pagy, setPagy] = useState<any>();

  const [loading, setLoading] = useState<boolean>(true);

  const company = useSelector((state: RootState) => state.company.company);
  const jobRoles = useSelector((state: RootState) => state.jobRole.jobRoles);

  const jobRoleOptions = [
    {label: 'All Job Roles', value: 'all'},
    ...jobRoles.map((jobRole: JobRole) => ({
      label: jobRole.name,
      value: jobRole.id,
    })),
  ];

  interface VisibleColumnsType {
    name: boolean;
    type: boolean;
    completedAt: boolean;
    status: boolean;
    user: boolean;
  }

  const [visibleColumns, setVisibleColumns] = useState<VisibleColumnsType>({
    name: true,
    type: true,
    completedAt: true,
    status: true,
    user: true,
  });

  const columnOptions = [
    {label: 'Name', value: 'name', isVisible: visibleColumns.name},
    {label: 'Type', value: 'type', isVisible: visibleColumns.type},
    {
      label: 'completedAt',
      value: 'completedAt',
      isVisible: visibleColumns.completedAt,
    },
    {label: 'User', value: 'user', isVisible: visibleColumns.user},
  ];

  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [downloadStatus, setDownloadStatus] =
    useState<DownloadStatus>('preparing');
  const [modalConfig, setModalConfig] = useState({
    title: 'Document Download',
    customMessages: {
      preparing: 'Preparing documents for processing...',
      processing: 'Processing and combining documents...',
      downloading: 'Starting download...',
      complete: 'Download complete!',
    },
    waitMessage: 'Please keep this page open until the download is complete',
    buttonText: 'Close',
  });

  const [isPrinting, setIsPrinting] = useState(false);
  const [printUrl, setPrintUrl] = useState<string | null>(null);
  const printIframeRef = useRef<HTMLIFrameElement>(null);

  const fetchAllData = async ({
    page,
    search,
    sortField,
    sortDirection,
    rowsPerPage,
  }: {
    page: number;
    search: SearchType<{[key: string]: SearchField}>;
    sortField: string;
    sortDirection: string;
    rowsPerPage: number;
  }): Promise<void> => {
    setLoading(true);

    const searchWithFilters: SearchType<{[key: string]: SearchField}> = {
      where: {
        ...search.where,
      },
    };

    if (selectedStatus !== 'all') {
      searchWithFilters.where.status = {
        operator: '==',
        value: selectedStatus,
      } as SearchField;
    }

    if (selectedDocumentType !== 'all') {
      searchWithFilters.where.document_type = {
        operator: '==',
        value: documentTypeMap[selectedDocumentType],
      } as SearchField;
    }

    if (selectedJobRole !== 'all') {
      searchWithFilters.where.job_role_id = {
        operator: '==',
        value: selectedJobRole,
      } as SearchField;
    }

    if (searchTerm && searchTerm.trim() !== '') {
      searchWithFilters.where.user_name = {
        operator: 'like',
        value: searchTerm.trim(),
      } as SearchField;
    }

    if (dateRangeValue && dateRangeValue.includes(' - ')) {
      const [startDate, endDate] = dateRangeValue.split(' - ');
      if (startDate && endDate) {
        const convertDateFormat = (dateStr: string): string => {
          const parsedDate = parse(dateStr, 'MM/dd/yyyy', new Date());
          const formattedDate = format(parsedDate, 'yyyy-MM-dd');
          return formattedDate;
        };

        const formattedStartDate = convertDateFormat(startDate);
        const formattedEndDate = convertDateFormat(endDate);

        searchWithFilters.where.completed_at = {
          operator: 'between',
          valueArray: [formattedStartDate, formattedEndDate],
        } as SearchField;
      }
    }

    await DocumentReportService.index(
      page,
      searchWithFilters,
      sortField,
      sortDirection,
      rowsPerPage,
    ).then(async (response: any) => {
      if (totalCount === response.totalCount) {
        setLoading(false);
      }
      setTotalCount(response.totalCount);
      setAllRecords(response.documents);
    });
  };

  useEffect(() => {
    setPagy({
      count: totalCount,
      pages: Math.ceil(totalCount / 10),
    });
    setLoading(false);
  }, [totalCount]);

  const handleColumnToggle = (column: string) => {
    if (column in visibleColumns) {
      setVisibleColumns(prev => ({
        ...prev,
        [column as keyof VisibleColumnsType]:
          !prev[column as keyof VisibleColumnsType],
      }));
    }
  };

  const handleStatusChange = (status: any) => {
    setSelectedStatus(status.value);
  };

  const handleDocumentTypeChange = (documentType: any) => {
    setSelectedDocumentType(documentType.value);
  };

  const handleJobRoleChange = (jobRole: any) => {
    setSelectedJobRole(jobRole.value);
  };

  const handleDateRangeChange = (startDate: string, endDate: string) => {
    if (startDate && endDate) {
      setDateRangeValue(`${startDate} - ${endDate}`);
    } else {
      setDateRangeValue('');
    }
  };

  const handleSearch = () => {
    setShowTable(true);
    setSearchTrigger(prev => prev + 1);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  useEffect(() => {
    if (showTable) {
      fetchAllData({
        page: 1,
        search: {where: {}},
        sortField: '',
        sortDirection: '',
        rowsPerPage: 10,
      });
    }
  }, [searchTrigger, showTable]);

  // Cargar job roles al montar el componente
  useEffect(() => {
    const loadJobRoles = async () => {
      try {
        await dispatch(
          JobRoleThunks.index({
            page: 1,
            search: {where: {}},
            sortField: '',
            sortDirection: '',
            rowsPerPage: 1000,
          }),
        );
      } catch (error) {
        console.error('Error loading job roles:', error);
      }
    };
    loadJobRoles();
  }, [dispatch]);

  const handleNameClick = (record: any) => {
    const documentType = record.documentType;

    const service = recordService(record);
    if (service) {
      service.show(record.id).then((response: any) => {
        openViewerModal({
          id: record.id,
          name: record.name,
          completedAt: record.completedAt,
          attachment: response.attachment,
          docType:
            documentType === 'Document' || documentType === 'Training'
              ? 'file'
              : 'template',
          recordType: record.documentType,
        });
      });
    }
  };

  const recordService = (record: any) => {
    switch (record.documentType) {
      case 'Document':
        return UserHrDocumentService;
      case 'Training':
        return UserTrainingService;
      case 'Application':
        return UserApplicationService;
      case 'Evaluation':
        return UserEvaluationService;
      case 'Role':
        return UserJobRoleService;
      default:
        return null;
    }
  };

  const handleDownload = async (record: any) => {
    const service = recordService(record);
    if (service) {
      try {
        const response = await service.show(record.id);
        if (
          record.documentType === 'Document' ||
          record.documentType === 'Training'
        ) {
          const attachmentUrl = response.attachment?.url;
          if (attachmentUrl) {
            window.open(attachmentUrl, '_blank');
          }
        } else {
          let templateData;

          if (response.documentTemplateSolution.snapshot) {
            templateData = response.documentTemplateSolution.snapshot;
          } else {
            alert('Could not generate document. Please try again.');
            return;
          }

          const template = new DocumentTemplate(templateData);
          const tempDiv = document.createElement('div');
          document.body.appendChild(tempDiv);

          const root = createRoot(tempDiv);
          root.render(
            <DocumentTemplateRenderer
              template={template}
              mode="download"
              companyInfo={{
                logoUrl: company.logoUrl,
                name: company.name,
              }}
              onPrintComplete={() => {
                root.unmount();
                document.body.removeChild(tempDiv);
              }}
            />,
          );
        }
      } catch (error) {
        alert('Oops, something went wrong. Please try again.');
      }
    }
  };

  const handleSelectAll = (checked: boolean) => {
    setIsAllSelected(checked);
    if (checked) {
      setSelectedRecords([...allRecords]);
    } else {
      setSelectedRecords([]);
    }
  };

  const handleSelectRecord = (record: any, checked: boolean) => {
    if (checked) {
      setSelectedRecords(prev => [...prev, record]);
    } else {
      setSelectedRecords(prev =>
        prev.filter(
          r => !(r.id === record.id && r.documentType === record.documentType),
        ),
      );
    }
    if (checked && selectedRecords.length + 1 === allRecords.length) {
      setIsAllSelected(true);
    } else if (!checked) {
      setIsAllSelected(false);
    }
  };

  const getTemplateHtml = async (record: any): Promise<string> => {
    return new Promise(resolve => {
      const service = recordService(record);
      if (service) {
        service.show(record.id).then((response: any) => {
          if (response.documentTemplateSolution?.snapshot) {
            const template = new DocumentTemplate(
              response.documentTemplateSolution.snapshot,
            );
            const tempDiv = document.createElement('div');
            document.body.appendChild(tempDiv);

            const root = createRoot(tempDiv);
            root.render(
              <Provider store={store}>
                <DocumentTemplateRenderer
                  template={template}
                  mode="view"
                  companyInfo={{
                    logoUrl: company.logoUrl,
                    name: company.name,
                  }}
                />
              </Provider>,
            );
            setTimeout(() => {
              const html = tempDiv.innerHTML;
              root.unmount();
              document.body.removeChild(tempDiv);
              resolve(html);
            }, 100);
          } else {
            resolve('');
          }
        });
      } else {
        resolve('');
      }
    });
  };

  const documentTypeMap: {[key: string]: string} = {
    Document: 'UserHrDocument',
    Training: 'UserTraining',
    Application: 'UserApplication',
    Evaluation: 'UserEvaluation',
    Credential: 'UserCredential',
    Role: 'UserJobRole',
  };

  const handleIframeLoad = () => {
    const iframe = printIframeRef.current;
    if (!iframe) return;

    try {
      if (iframe.contentWindow?.document.body) {
        iframe.contentWindow.print();
        setTimeout(() => {
          setPrintUrl(null);
          setIsPrinting(false);
        }, 1000);
      } else {
        throw new Error('There is no content');
      }
    } catch (error) {
      setPrintUrl(null);
      setIsPrinting(false);
    }
  };

  const processDocuments = async (action: 'download' | 'print' | 'view') => {
    if (selectedRecords.length === 0) {
      alert(`Please select at least one document to ${action}.`);
      return;
    }
    try {
      if (action === 'download') {
        setModalConfig({
          title: 'Document Download',
          customMessages: {
            preparing: 'Preparing documents for download...',
            processing: 'Processing and combining documents...',
            downloading: 'Starting download...',
            complete: 'Download complete!',
          },
          waitMessage:
            'Please keep this page open until the download is complete',
          buttonText: 'Close',
        });
      } else if (action === 'view') {
        setModalConfig({
          title: 'Document Preview',
          customMessages: {
            preparing: 'Preparing documents for viewing...',
            processing: 'Processing and combining documents...',
            downloading: 'Preparing document viewer...',
            complete: 'Documents ready to view!',
          },
          waitMessage:
            'Please keep this page open while we prepare your documents',
          buttonText: 'Close',
        });
      } else {
        setModalConfig({
          title: 'Document Print',
          customMessages: {
            preparing: 'Preparing documents for printing...',
            processing: 'Processing and combining documents...',
            downloading: 'Preparing print layout...',
            complete: 'Documents ready to print!',
          },
          waitMessage:
            'Please keep this page open while we prepare your documents for printing',
          buttonText: 'Close',
        });
      }
      setIsDownloadModalOpen(true);
      setDownloadStatus('preparing');

      const documentsToDownload = await Promise.all(
        selectedRecords.map(async record => {
          const document: any = {
            id: record.id,
            documentType:
              documentTypeMap[record.documentType] || record.documentType,
          };

          if (
            record.documentType !== 'Document' &&
            record.documentType !== 'Training'
          ) {
            document.htmlRender = await getTemplateHtml(record);
          }

          return document;
        }),
      );

      setDownloadStatus('processing');

      const response =
        await DocumentReportService.batchDownload(documentsToDownload);

      if (!response.data?.url) {
        throw new Error('No valid download URL received');
      }

      setDownloadStatus('downloading');

      if (action === 'download') {
        const link = document.createElement('a');
        link.href = response.data.url;
        link.download = `combined_documents_${new Date().toISOString().split('T')[0]}.pdf`;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else if (action === 'view') {
        openViewerModal({
          id: 'batch-view',
          name: `Selected documents (${selectedRecords.length})`,
          completedAt: new Date().toISOString(),
          attachment: {url: response.data.url},
          docType: 'file',
          recordType: 'BatchDocument',
        });
      } else {
        const printWindow = window.open('', '_blank');
        if (!printWindow) {
          throw new Error('Failed to open the print window');
        }
        printWindow.document.write(`
          <html>
            <head>
              <title>Imprint Document</title>
              <style>
                @page {
                  size: A4;
                  margin: 0;
                }
                body, html {
                  margin: 0;
                  padding: 0;
                  width: 210mm;
                  height: 297mm;
                  overflow: hidden;
                }
                iframe {
                  position: fixed;
                  top: 0;
                  left: 0;
                  width: 210mm;
                  height: 297mm;
                  border: none;
                  box-sizing: border-box;
                }
                @media print {
                  @page {
                    size: A4;
                    margin: 0;
                  }
                  body {
                    margin: 0;
                    padding: 0;
                    width: 210mm;
                    height: 297mm;
                    -webkit-print-color-adjust: exact;
                    print-color-adjust: exact;
                  }
                  iframe {
                    position: absolute;
                    width: 210mm;
                    height: 297mm;
                    object-fit: contain;
                  }
                }
              </style>
              <script>
                function checkPDFLoaded(iframe) {
                  try {
                    if (iframe.contentWindow.document.readyState === 'complete' && 
                        iframe.contentWindow.document.body && 
                        iframe.contentWindow.document.body.innerHTML !== '') {
                      setTimeout(() => {
                        window.print();
                        setTimeout(() => window.close(), 1000);
                      }, 500);
                    } else {
                      setTimeout(() => checkPDFLoaded(iframe), 100);
                    }
                  } catch (e) {
                    setTimeout(() => {
                      window.print();
                      setTimeout(() => window.close(), 1000);
                    }, 2000);
                  }
                }
              </script>
            </head>
            <body>
              <iframe 
                src="${response.data.url}#toolbar=0&navpanes=0&view=Fit" 
                onload="checkPDFLoaded(this)"
              ></iframe>
            </body>
          </html>
        `);
        printWindow.document.close();
      }

      setDownloadStatus('complete');

      setTimeout(() => {
        setIsDownloadModalOpen(false);
        setDownloadStatus('preparing');
      }, 2000);
    } catch (error) {
      if (error instanceof Error) {
      }
      alert(
        `An error occurred while ${action}ing the documents. Please try again.`,
      );
      setIsDownloadModalOpen(false);
      setDownloadStatus('preparing');
    }
  };

  useEffect(() => {
    return () => {
      setPrintUrl(null);
      setIsPrinting(false);
    };
  }, []);

  const handleSelectDownload = () => {
    processDocuments('download');
  };
  const handleViewSelected = () => {
    processDocuments('view');
  };

  const getUserDisplay = (record: any) => {
    return `${record.userName}`.trim() || 'N/A';
  };

  const hasSelectedRecordsWithMissingStatus = () => {
    return selectedRecords.some(record => record.status === 'Missing');
  };

  return (
    <>
      <DownloadModal
        isOpen={isDownloadModalOpen}
        onClose={() => {
          setIsDownloadModalOpen(false);
          setDownloadStatus('preparing');
        }}
        status={downloadStatus}
        title={modalConfig.title}
        customMessages={modalConfig.customMessages}
        waitMessage={modalConfig.waitMessage}
        buttonText={modalConfig.buttonText}
      />
      {isPrinting && printUrl && (
        <iframe
          ref={printIframeRef}
          src={printUrl}
          style={{display: 'none'}}
          title="print-frame"
          onLoad={handleIframeLoad}
        />
      )}

      <div className="p-5 bg-white border border-gray-200 rounded-xl dark:bg-neutral-800 dark:border-neutral-700 mb-8">
        <div className="flex gap-4 items-start">
          <div className="flex-1">
            <div className="relative">
              <input
                type="text"
                className="py-[7px] pl-10 pr-3 md:pr-24 block w-full bg-gray-50 border border-gray-200 rounded-lg text-sm focus:border-cyan-400 focus:outline-cyan-400
                     focus:ring-cyan-400 disabled:opacity-50 disabled:pointer-events-none placeholder:text-slate-400
                     dark:bg-neutral-700 dark:border-transparent dark:text-neutral-400 dark:placeholder:text-neutral-400 dark:focus:ring-neutral-600"
                placeholder={`Search by name`}
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                onKeyDown={handleKeyPress}
              />
              <div className="absolute text-slate-400 inset-y-0 start-0 pt-2 pointer-events-none z-10 ps-3.5">
                <SearchIcon />
              </div>
            </div>
          </div>

          <div className="w-auto">
            <DateRangeInput
              key={'dateRange'}
              id="dateRange"
              type="date"
              value={dateRangeValue}
              onChange={handleDateRangeChange}
              placeholder="MM/DD/YYYY"
              calendarAlignment={'left'}
              allowPastDates={true}
              mode={'past'}
            />
          </div>

          <div className="w-48">
            <div className="flex flex-col gap-[0.625rem] w-full">
              <CustomSelect
                options={statusOptions}
                value={selectedStatus}
                onChange={handleStatusChange}
              />
            </div>
          </div>

          <div className="w-48">
            <div className="flex flex-col gap-[0.625rem] w-full">
              <CustomSelect
                options={documentTypeOptions}
                value={selectedDocumentType}
                onChange={handleDocumentTypeChange}
              />
            </div>
          </div>

          <div className="w-48">
            <div className="flex flex-col gap-[0.625rem] w-full">
              <CustomSelect
                options={jobRoleOptions}
                value={selectedJobRole}
                onChange={handleJobRoleChange}
              />
            </div>
          </div>

          <div className="w-auto">
            <BlueButton
              onClick={handleSearch}
              label="Search"
              inputId="search-button"
            />
          </div>
        </div>
      </div>

      {showTable && (
        <Table
          fetchData={fetchAllData}
          pagy={pagy}
          recordsName={'Documents'}
          loading={loading}
          hideHeaders={true}
          columns={true}
          dateRange={true}
          columnOptions={columnOptions}
          optionsByTab={{
            default: {
              dateRange: true,
              dateRangeKey: 'completed_at',
              dateRangeMode: 'past',
              compressedButton: true,
            },
          }}
          searchByOptions={[
            {
              label: 'Name',
              value: 'name',
              operator: 'like',
            },
            {
              label: 'User',
              value: 'user',
              operator: 'like',
            },
            {
              label: 'Email',
              value: 'email',
              operator: 'like',
            },
          ]}
          onColumnToggle={handleColumnToggle}>
          <thead>
            <tr>
              <th scope="col" className="px-3 pt-3.5 flex justify-center">
                <input
                  type="checkbox"
                  checked={isAllSelected}
                  onChange={e => handleSelectAll(e.target.checked)}
                  className="shrink-0 border-stone-300 rounded disabled:opacity-50 disabled:pointer-events-none
                    dark:bg-neutral-800 dark:border-neutral-600 dark:checked:bg-green-500
                    dark:checked:border-green-500 dark:focus:ring-offset-neutral-800"
                />
              </th>
              {visibleColumns.name && (
                <TableHeader label="Name" sortBy="name" />
              )}
              {visibleColumns.type && (
                <TableHeader label="Type" sortBy="documentType" />
              )}
              {visibleColumns.status && (
                <TableHeader label="Status" sortBy="status" />
              )}
              {visibleColumns.completedAt && (
                <TableHeader label="Completed At" sortBy="completedAt" />
              )}
              {visibleColumns.user && (
                <TableHeader label="User" sortBy="user" />
              )}
              <th>
                <div className="flex gap-[5px] justify-start ">
                  <WhiteButton
                    onClick={handleViewSelected}
                    icon={<ViewIcon />}
                    disabled={
                      selectedRecords.length === 0 ||
                      hasSelectedRecordsWithMissingStatus()
                    }
                    className="w-9 h-9"
                  />
                  <WhiteButton
                    onClick={handleSelectDownload}
                    icon={<DownloadIcon />}
                    disabled={
                      selectedRecords.length === 0 ||
                      hasSelectedRecordsWithMissingStatus()
                    }
                    className="w-9 h-9"
                  />
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {allRecords?.length !== 0 &&
              allRecords?.map((record: any) => {
                const userOptions = [];

                userOptions.push({
                  label: 'Download',
                  onClick: () => handleDownload(record),
                  hoverClass: 'hover:bg-cyan-100',
                });

                return (
                  <TableRow key={`${record.id}-${record.documentType}`}>
                    <td className="px-3 py-4 flex justify-center">
                      <input
                        type="checkbox"
                        checked={selectedRecords.some(
                          r =>
                            r.id === record.id &&
                            r.documentType === record.documentType,
                        )}
                        onChange={e =>
                          handleSelectRecord(record, e.target.checked)
                        }
                        className="border border-gray-300 rounded"
                      />
                    </td>
                    {visibleColumns.name && (
                      <TableCell>
                        <span
                          className={`text-sm font-medium underline text-cyan-600 cursor-pointer`}
                          onClick={() => handleNameClick(record)}>
                          {record.name}
                        </span>
                      </TableCell>
                    )}
                    {visibleColumns.type && (
                      <TableCell>{record.documentType}</TableCell>
                    )}
                    {visibleColumns.status && (
                      <TableCell>
                        <StatusBadge status={record.status} />
                      </TableCell>
                    )}
                    {visibleColumns.completedAt && (
                      <TableCell>{record.completedAt}</TableCell>
                    )}
                    {visibleColumns.user && (
                      <TableCell>{getUserDisplay(record)}</TableCell>
                    )}
                    <TableCell>
                      <DotsMenu options={userOptions} />
                    </TableCell>
                  </TableRow>
                );
              })}
          </tbody>
        </Table>
      )}
    </>
  );
};

export default ReportFinderTable;
